import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { customFieldsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Plus, Trash2, Loader2, Pencil, Settings, GripVertical, X, ChevronDown, ChevronUp } from 'lucide-react';

const CLIENT_TYPES = [
  { id: 'Individual', label: 'Individual' },
  { id: 'Corporation', label: 'Corporation' },
  { id: 'Others', label: 'Others' }
];

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'dropdown', label: 'Dropdown (Selectable List)' },
  { value: 'conditional_number', label: 'Conditional (Number → Multiple Items)' },
  { value: 'conditional_dropdown', label: 'Conditional (Dropdown → Show Fields)' }
];

const SUB_FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Checkbox' }
];

const CustomFields = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [newField, setNewField] = useState({
    name: '',
    type: 'text',
    required: false,
    appliesTo: [],
    options: [],
    conditionalConfig: {
      triggerType: 'number',
      maxCount: 10,
      subFields: [],
      conditions: {}
    }
  });
  const { toast } = useToast();

  const generateSubFieldId = () => `sf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const response = await customFieldsAPI.getAll();
      setFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateOptionId = () => `opt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleCreateField = async () => {
    if (!newField.name) {
      toast({ title: 'Error', description: 'Field name is required', variant: 'destructive' });
      return;
    }

    if (newField.type === 'dropdown' && (!newField.options || newField.options.length === 0)) {
      toast({ title: 'Error', description: 'Please add at least one dropdown option', variant: 'destructive' });
      return;
    }

    if (newField.type === 'conditional_number' && (!newField.conditionalConfig?.subFields || newField.conditionalConfig.subFields.length === 0)) {
      toast({ title: 'Error', description: 'Please add at least one sub-field for the conditional field', variant: 'destructive' });
      return;
    }

    if (newField.type === 'conditional_dropdown') {
      if (!newField.conditionalConfig?.dropdownOptions || newField.conditionalConfig.dropdownOptions.length === 0) {
        toast({ title: 'Error', description: 'Please add at least one dropdown option', variant: 'destructive' });
        return;
      }
      // Check that each option has at least one sub-field
      const hasEmptyCondition = newField.conditionalConfig.dropdownOptions.some(opt => {
        const subFields = newField.conditionalConfig.conditions?.[opt.id] || [];
        return subFields.length === 0;
      });
      if (hasEmptyCondition) {
        toast({ title: 'Error', description: 'Each dropdown option must have at least one sub-field', variant: 'destructive' });
        return;
      }
    }

    try {
      const fieldId = newField.name.toLowerCase().replace(/\s+/g, '_');
      const fieldData = {
        ...newField,
        fieldId,
        options: newField.options.map((opt, idx) => ({
          ...opt,
          order: idx
        }))
      };
      
      // Only include conditionalConfig for conditional field types
      if (!newField.type.startsWith('conditional_')) {
        delete fieldData.conditionalConfig;
      }
      
      await customFieldsAPI.create(fieldData);
      toast({ title: 'Success', description: 'Custom field created' });
      setCreateDialog(false);
      setNewField({ 
        name: '', 
        type: 'text', 
        required: false, 
        appliesTo: [], 
        options: [],
        conditionalConfig: { triggerType: 'number', maxCount: 10, subFields: [], conditions: {} }
      });
      fetchFields();
    } catch (error) {
      console.error('Error creating field:', error);
      toast({ title: 'Error', description: 'Failed to create field', variant: 'destructive' });
    }
  };

  const handleUpdateField = async () => {
    if (!editingField?.name) {
      toast({ title: 'Error', description: 'Field name is required', variant: 'destructive' });
      return;
    }

    if (editingField.type === 'dropdown' && (!editingField.options || editingField.options.length === 0)) {
      toast({ title: 'Error', description: 'Please add at least one dropdown option', variant: 'destructive' });
      return;
    }

    if (editingField.type === 'conditional_number' && (!editingField.conditionalConfig?.subFields || editingField.conditionalConfig.subFields.length === 0)) {
      toast({ title: 'Error', description: 'Please add at least one sub-field for the conditional field', variant: 'destructive' });
      return;
    }

    if (editingField.type === 'conditional_dropdown') {
      if (!editingField.conditionalConfig?.dropdownOptions || editingField.conditionalConfig.dropdownOptions.length === 0) {
        toast({ title: 'Error', description: 'Please add at least one dropdown option', variant: 'destructive' });
        return;
      }
      const hasEmptyCondition = editingField.conditionalConfig.dropdownOptions.some(opt => {
        const subFields = editingField.conditionalConfig.conditions?.[opt.id] || [];
        return subFields.length === 0;
      });
      if (hasEmptyCondition) {
        toast({ title: 'Error', description: 'Each dropdown option must have at least one sub-field', variant: 'destructive' });
        return;
      }
    }

    try {
      const updateData = {
        name: editingField.name,
        type: editingField.type,
        required: editingField.required,
        appliesTo: editingField.appliesTo || [],
        options: (editingField.options || []).map((opt, idx) => ({
          ...opt,
          order: idx
        }))
      };
      
      // Only include conditionalConfig for conditional field types
      if (editingField.type.startsWith('conditional_')) {
        updateData.conditionalConfig = editingField.conditionalConfig;
      }
      
      await customFieldsAPI.update(editingField._id, updateData);
      toast({ title: 'Success', description: 'Custom field updated' });
      setEditDialog(false);
      setEditingField(null);
      fetchFields();
    } catch (error) {
      console.error('Error updating field:', error);
      toast({ title: 'Error', description: 'Failed to update field', variant: 'destructive' });
    }
  };

  const handleDeleteField = async (id, isSystem) => {
    if (isSystem) {
      toast({ title: 'Error', description: 'Cannot delete system fields', variant: 'destructive' });
      return;
    }
    if (!window.confirm('Are you sure you want to delete this field?')) return;

    try {
      await customFieldsAPI.delete(id);
      toast({ title: 'Success', description: 'Custom field deleted' });
      fetchFields();
    } catch (error) {
      console.error('Error deleting field:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete field', variant: 'destructive' });
    }
  };

  const toggleAppliesTo = (typeId, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        appliesTo: prev.appliesTo.includes(typeId)
          ? prev.appliesTo.filter(t => t !== typeId)
          : [...prev.appliesTo, typeId]
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        appliesTo: (prev.appliesTo || []).includes(typeId)
          ? prev.appliesTo.filter(t => t !== typeId)
          : [...(prev.appliesTo || []), typeId]
      }));
    }
  };

  const addDropdownOption = (isCreate = true) => {
    const newOption = {
      id: generateOptionId(),
      label: '',
      order: 0,
      allowFreeText: false
    };

    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        options: [...(prev.options || []), newOption]
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        options: [...(prev.options || []), newOption]
      }));
    }
  };

  const updateDropdownOption = (index, field, value, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        options: prev.options.map((opt, i) => 
          i === index ? { ...opt, [field]: value } : opt
        )
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        options: (prev.options || []).map((opt, i) => 
          i === index ? { ...opt, [field]: value } : opt
        )
      }));
    }
  };

  const removeDropdownOption = (index, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        options: (prev.options || []).filter((_, i) => i !== index)
      }));
    }
  };

  const moveDropdownOption = (index, direction, isCreate = true) => {
    const move = (options) => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= options.length) return options;
      const newOptions = [...options];
      [newOptions[index], newOptions[newIndex]] = [newOptions[newIndex], newOptions[index]];
      return newOptions;
    };

    if (isCreate) {
      setNewField(prev => ({ ...prev, options: move(prev.options || []) }));
    } else {
      setEditingField(prev => ({ ...prev, options: move(prev.options || []) }));
    }
  };

  // Sub-fields management for conditional fields
  const addSubField = (isCreate = true) => {
    const newSubField = {
      id: generateSubFieldId(),
      name: '',
      type: 'text',
      required: false
    };

    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          subFields: [...(prev.conditionalConfig?.subFields || []), newSubField]
        }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          subFields: [...(prev.conditionalConfig?.subFields || []), newSubField]
        }
      }));
    }
  };

  const updateSubField = (index, field, value, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          subFields: (prev.conditionalConfig?.subFields || []).map((sf, i) =>
            i === index ? { ...sf, [field]: value } : sf
          )
        }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          subFields: (prev.conditionalConfig?.subFields || []).map((sf, i) =>
            i === index ? { ...sf, [field]: value } : sf
          )
        }
      }));
    }
  };

  const removeSubField = (index, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          subFields: (prev.conditionalConfig?.subFields || []).filter((_, i) => i !== index)
        }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          subFields: (prev.conditionalConfig?.subFields || []).filter((_, i) => i !== index)
        }
      }));
    }
  };

  const updateMaxCount = (value, isCreate = true) => {
    const maxCount = Math.min(Math.max(1, parseInt(value) || 1), 10);
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: { ...prev.conditionalConfig, maxCount }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: { ...prev.conditionalConfig, maxCount }
      }));
    }
  };

  // Conditional Dropdown management
  const addDropdownOptionForCondition = (isCreate = true) => {
    const newOption = {
      id: generateOptionId(),
      label: '',
      order: 0
    };

    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          dropdownOptions: [...(prev.conditionalConfig?.dropdownOptions || []), newOption],
          conditions: { ...prev.conditionalConfig?.conditions, [newOption.id]: [] }
        }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          dropdownOptions: [...(prev.conditionalConfig?.dropdownOptions || []), newOption],
          conditions: { ...prev.conditionalConfig?.conditions, [newOption.id]: [] }
        }
      }));
    }
  };

  const updateDropdownOptionForCondition = (optionId, label, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          dropdownOptions: (prev.conditionalConfig?.dropdownOptions || []).map(opt =>
            opt.id === optionId ? { ...opt, label } : opt
          )
        }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          dropdownOptions: (prev.conditionalConfig?.dropdownOptions || []).map(opt =>
            opt.id === optionId ? { ...opt, label } : opt
          )
        }
      }));
    }
  };

  const removeDropdownOptionForCondition = (optionId, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => {
        const newConditions = { ...prev.conditionalConfig?.conditions };
        delete newConditions[optionId];
        return {
          ...prev,
          conditionalConfig: {
            ...prev.conditionalConfig,
            dropdownOptions: (prev.conditionalConfig?.dropdownOptions || []).filter(opt => opt.id !== optionId),
            conditions: newConditions
          }
        };
      });
    } else {
      setEditingField(prev => {
        const newConditions = { ...prev.conditionalConfig?.conditions };
        delete newConditions[optionId];
        return {
          ...prev,
          conditionalConfig: {
            ...prev.conditionalConfig,
            dropdownOptions: (prev.conditionalConfig?.dropdownOptions || []).filter(opt => opt.id !== optionId),
            conditions: newConditions
          }
        };
      });
    }
  };

  const addSubFieldToCondition = (optionId, isCreate = true) => {
    const newSubField = {
      id: generateSubFieldId(),
      name: '',
      type: 'text',
      required: false
    };

    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          conditions: {
            ...prev.conditionalConfig?.conditions,
            [optionId]: [...(prev.conditionalConfig?.conditions?.[optionId] || []), newSubField]
          }
        }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          conditions: {
            ...prev.conditionalConfig?.conditions,
            [optionId]: [...(prev.conditionalConfig?.conditions?.[optionId] || []), newSubField]
          }
        }
      }));
    }
  };

  const updateSubFieldInCondition = (optionId, subFieldIndex, field, value, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          conditions: {
            ...prev.conditionalConfig?.conditions,
            [optionId]: (prev.conditionalConfig?.conditions?.[optionId] || []).map((sf, i) =>
              i === subFieldIndex ? { ...sf, [field]: value } : sf
            )
          }
        }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          conditions: {
            ...prev.conditionalConfig?.conditions,
            [optionId]: (prev.conditionalConfig?.conditions?.[optionId] || []).map((sf, i) =>
              i === subFieldIndex ? { ...sf, [field]: value } : sf
            )
          }
        }
      }));
    }
  };

  const removeSubFieldFromCondition = (optionId, subFieldIndex, isCreate = true) => {
    if (isCreate) {
      setNewField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          conditions: {
            ...prev.conditionalConfig?.conditions,
            [optionId]: (prev.conditionalConfig?.conditions?.[optionId] || []).filter((_, i) => i !== subFieldIndex)
          }
        }
      }));
    } else {
      setEditingField(prev => ({
        ...prev,
        conditionalConfig: {
          ...prev.conditionalConfig,
          conditions: {
            ...prev.conditionalConfig?.conditions,
            [optionId]: (prev.conditionalConfig?.conditions?.[optionId] || []).filter((_, i) => i !== subFieldIndex)
          }
        }
      }));
    }
  };

  const openEditDialog = (field) => {
    setEditingField({
      ...field,
      appliesTo: field.appliesTo || [],
      options: field.options || [],
      conditionalConfig: field.conditionalConfig || {
        triggerType: 'number',
        maxCount: 10,
        subFields: [],
        conditions: {}
      }
    });
    setEditDialog(true);
  };

  const getAppliesToDisplay = (appliesTo) => {
    if (!appliesTo || appliesTo.length === 0) {
      return <Badge variant="outline">All Types</Badge>;
    }
    return appliesTo.map(type => (
      <Badge key={type} variant="secondary" className="mr-1">
        {type}
      </Badge>
    ));
  };

  const renderOptionsManager = (options, isCreate = true) => (
    <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Dropdown Options</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addDropdownOption(isCreate)}
          className="gap-1"
        >
          <Plus className="h-3 w-3" /> Add Option
        </Button>
      </div>
      
      {(options || []).length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No options added yet. Click "Add Option" to create dropdown choices.
        </p>
      ) : (
        <div className="space-y-2">
          {options.map((option, index) => (
            <div key={option.id || index} className="flex items-center gap-2 bg-white p-2 rounded border">
              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => moveDropdownOption(index, 'up', isCreate)}
                  disabled={index === 0}
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => moveDropdownOption(index, 'down', isCreate)}
                  disabled={index === options.length - 1}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>
              <Input
                placeholder="Option label"
                value={option.label}
                onChange={(e) => updateDropdownOption(index, 'label', e.target.value, isCreate)}
                className="flex-1"
              />
              <div className="flex items-center gap-1">
                <Checkbox
                  id={`freetext-${option.id || index}`}
                  checked={option.allowFreeText}
                  onCheckedChange={(checked) => updateDropdownOption(index, 'allowFreeText', checked, isCreate)}
                />
                <Label htmlFor={`freetext-${option.id || index}`} className="text-xs">Free text</Label>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500"
                onClick={() => removeDropdownOption(index, isCreate)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render conditional field configuration
  const renderConditionalConfig = (config, isCreate = true) => (
    <div className="space-y-4 border rounded-lg p-4 bg-blue-50">
      <div className="flex items-center gap-2">
        <Settings className="h-5 w-5 text-blue-600" />
        <Label className="text-sm font-medium text-blue-900">Conditional Field Configuration</Label>
      </div>
      
      <div className="bg-white p-3 rounded-lg border space-y-3">
        <p className="text-sm text-gray-600">
          This field will ask for a number first. Based on that number (1-{config?.maxCount || 10}), 
          it will show that many sets of sub-fields for data entry.
        </p>
        
        <div className="space-y-2">
          <Label className="text-sm">Maximum Count (1-10)</Label>
          <Input
            type="number"
            min="1"
            max="10"
            value={config?.maxCount || 10}
            onChange={(e) => updateMaxCount(e.target.value, isCreate)}
            className="w-24"
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Sub-Fields (shown for each item)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addSubField(isCreate)}
            className="gap-1"
          >
            <Plus className="h-3 w-3" /> Add Sub-Field
          </Button>
        </div>

        {(config?.subFields || []).length === 0 ? (
          <div className="text-center py-6 bg-white rounded-lg border border-dashed">
            <p className="text-sm text-gray-500">
              No sub-fields added yet. Add fields like "Name", "Date of Birth", "SIN" etc.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {config.subFields.map((subField, index) => (
              <div key={subField.id || index} className="flex items-center gap-2 bg-white p-3 rounded-lg border">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Field name (e.g., Name)"
                    value={subField.name}
                    onChange={(e) => updateSubField(index, 'name', e.target.value, isCreate)}
                  />
                  <Select
                    value={subField.type}
                    onValueChange={(v) => updateSubField(index, 'type', v, isCreate)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUB_FIELD_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Checkbox
                    id={`req-${subField.id || index}`}
                    checked={subField.required}
                    onCheckedChange={(checked) => updateSubField(index, 'required', checked, isCreate)}
                  />
                  <Label htmlFor={`req-${subField.id || index}`} className="text-xs">Required</Label>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500"
                  onClick={() => removeSubField(index, isCreate)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
        <p className="text-xs text-yellow-800">
          <strong>Example:</strong> For a "Children" field with sub-fields "Name", "DOB", and "SIN":
          If user enters "2", they will see 2 sets of (Name, DOB, SIN) inputs.
        </p>
      </div>
    </div>
  );

  // Render conditional dropdown configuration
  const renderConditionalDropdownConfig = (config, isCreate = true) => (
    <div className="space-y-4 border rounded-lg p-4 bg-purple-50">
      <div className="flex items-center gap-2">
        <Settings className="h-5 w-5 text-purple-600" />
        <Label className="text-sm font-medium text-purple-900">Conditional Dropdown Configuration</Label>
      </div>
      
      <div className="bg-white p-3 rounded-lg border space-y-3">
        <p className="text-sm text-gray-600">
          Create a dropdown where each option shows different sub-fields when selected.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Dropdown Options & Their Fields</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addDropdownOptionForCondition(isCreate)}
            className="gap-1"
          >
            <Plus className="h-3 w-3" /> Add Option
          </Button>
        </div>

        {(config?.dropdownOptions || []).length === 0 ? (
          <div className="text-center py-6 bg-white rounded-lg border border-dashed">
            <p className="text-sm text-gray-500">
              No options added yet. Add dropdown options and configure which fields appear for each.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {(config?.dropdownOptions || []).map((option, optIndex) => (
              <div key={option.id} className="bg-white rounded-lg border p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-purple-700">{optIndex + 1}</span>
                  </div>
                  <Input
                    placeholder="Option label (e.g., Employed, Self-Employed)"
                    value={option.label}
                    onChange={(e) => updateDropdownOptionForCondition(option.id, e.target.value, isCreate)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500"
                    onClick={() => removeDropdownOptionForCondition(option.id, isCreate)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Sub-fields for this option */}
                <div className="ml-8 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-500">Fields shown when "{option.label || 'this option'}" is selected:</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => addSubFieldToCondition(option.id, isCreate)}
                      className="gap-1 h-7 text-xs"
                    >
                      <Plus className="h-3 w-3" /> Add Field
                    </Button>
                  </div>

                  {(config?.conditions?.[option.id] || []).length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No fields configured for this option</p>
                  ) : (
                    <div className="space-y-2">
                      {(config?.conditions?.[option.id] || []).map((subField, sfIndex) => (
                        <div key={subField.id} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                          <Input
                            placeholder="Field name"
                            value={subField.name}
                            onChange={(e) => updateSubFieldInCondition(option.id, sfIndex, 'name', e.target.value, isCreate)}
                            className="flex-1 h-8 text-sm"
                          />
                          <Select
                            value={subField.type}
                            onValueChange={(v) => updateSubFieldInCondition(option.id, sfIndex, 'type', v, isCreate)}
                          >
                            <SelectTrigger className="w-24 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SUB_FIELD_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-1">
                            <Checkbox
                              id={`req-${option.id}-${sfIndex}`}
                              checked={subField.required}
                              onCheckedChange={(checked) => updateSubFieldInCondition(option.id, sfIndex, 'required', checked, isCreate)}
                            />
                            <Label htmlFor={`req-${option.id}-${sfIndex}`} className="text-xs">Req</Label>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500"
                            onClick={() => removeSubFieldFromCondition(option.id, sfIndex, isCreate)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
        <p className="text-xs text-yellow-800">
          <strong>Example:</strong> For "Employment Status" with options "Employed" and "Self-Employed":
          <br/>• "Employed" → shows: Employer Name, Position, Start Date
          <br/>• "Self-Employed" → shows: Business Name, Business Type, Registration #
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Custom Fields</h1>
          <p className="text-gray-500 mt-1">Manage custom fields that appear on client profiles</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} className="gap-2" data-testid="add-custom-field-btn">
          <Plus className="h-4 w-4" />
          Add Custom Field
        </Button>
      </div>

      {/* Info card about visibility */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Settings className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Field Visibility & Dropdown Support</p>
              <p className="text-sm text-blue-700 mt-1">
                Configure which client types can see each field. Use "Dropdown" type to create selectable lists with custom options.
                Dropdown options can be reordered and edited without affecting existing client data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Field Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Options</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Applies To</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field) => (
                <TableRow key={field._id} data-testid={`custom-field-row-${field._id}`}>
                  <TableCell className="font-medium">
                    {field.name}
                    {field.isSystem && (
                      <Badge variant="outline" className="ml-2 text-xs">System</Badge>
                    )}
                  </TableCell>
                  <TableCell className="capitalize">{field.type}</TableCell>
                  <TableCell>
                    {field.type === 'dropdown' && field.options?.length > 0 ? (
                      <span className="text-sm text-gray-500">
                        {field.options.length} option{field.options.length !== 1 ? 's' : ''}
                      </span>
                    ) : field.type === 'conditional_number' && field.conditionalConfig?.subFields?.length > 0 ? (
                      <span className="text-sm text-gray-500">
                        {field.conditionalConfig.subFields.length} sub-field{field.conditionalConfig.subFields.length !== 1 ? 's' : ''}, max {field.conditionalConfig.maxCount || 10}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{field.required ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{getAppliesToDisplay(field.appliesTo)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(field)}
                        className="text-gray-500 hover:text-gray-700"
                        data-testid={`edit-field-${field._id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteField(field._id, field.isSystem)}
                        className="text-red-500 hover:text-red-700"
                        disabled={field.isSystem}
                        data-testid={`delete-field-${field._id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {fields.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No custom fields created yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Custom Field</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Field Name *</Label>
              <Input
                placeholder="e.g., Referral Source"
                value={newField.name}
                onChange={(e) => setNewField(prev => ({ ...prev, name: e.target.value }))}
                data-testid="new-field-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Field Type</Label>
              <Select 
                value={newField.type} 
                onValueChange={(v) => setNewField(prev => ({ ...prev, type: v, options: v === 'dropdown' ? prev.options : [] }))}
              >
                <SelectTrigger data-testid="new-field-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dropdown Options Manager */}
            {newField.type === 'dropdown' && renderOptionsManager(newField.options, true)}

            {/* Conditional Field Config */}
            {newField.type === 'conditional_number' && renderConditionalConfig(newField.conditionalConfig, true)}

            {/* Conditional Dropdown Config */}
            {newField.type === 'conditional_dropdown' && renderConditionalDropdownConfig(newField.conditionalConfig, true)}

            <div className="flex items-center gap-2">
              <Checkbox
                id="newFieldRequired"
                checked={newField.required}
                onCheckedChange={(checked) => setNewField(prev => ({ ...prev, required: checked }))}
              />
              <Label htmlFor="newFieldRequired">Required field</Label>
            </div>
            <div className="space-y-2">
              <Label>Applies to Client Types</Label>
              <p className="text-xs text-gray-500 mb-2">Select which client types should see this field (leave empty for all)</p>
              <div className="flex flex-wrap gap-4">
                {CLIENT_TYPES.map((type) => (
                  <div key={type.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`new-${type.id}`}
                      checked={newField.appliesTo.includes(type.id)}
                      onCheckedChange={() => toggleAppliesTo(type.id, true)}
                      data-testid={`new-applies-${type.id}`}
                    />
                    <Label htmlFor={`new-${type.id}`} className="text-sm">{type.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateField} data-testid="create-field-btn">
              Create Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Custom Field</DialogTitle>
          </DialogHeader>
          {editingField && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Field Name *</Label>
                <Input
                  value={editingField.name}
                  onChange={(e) => setEditingField(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="edit-field-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Field Type</Label>
                <Select 
                  value={editingField.type} 
                  onValueChange={(v) => setEditingField(prev => ({ ...prev, type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dropdown Options Manager */}
              {editingField.type === 'dropdown' && renderOptionsManager(editingField.options, false)}

              {/* Conditional Field Config */}
              {editingField.type === 'conditional_number' && renderConditionalConfig(editingField.conditionalConfig, false)}

              {/* Conditional Dropdown Config */}
              {editingField.type === 'conditional_dropdown' && renderConditionalDropdownConfig(editingField.conditionalConfig, false)}

              <div className="flex items-center gap-2">
                <Checkbox
                  id="editFieldRequired"
                  checked={editingField.required}
                  onCheckedChange={(checked) => setEditingField(prev => ({ ...prev, required: checked }))}
                />
                <Label htmlFor="editFieldRequired">Required field</Label>
              </div>
              <div className="space-y-2">
                <Label>Applies to Client Types</Label>
                <p className="text-xs text-gray-500 mb-2">Select which client types should see this field (leave empty for all)</p>
                <div className="flex flex-wrap gap-4">
                  {CLIENT_TYPES.map((type) => (
                    <div key={type.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-${type.id}`}
                        checked={(editingField.appliesTo || []).includes(type.id)}
                        onCheckedChange={() => toggleAppliesTo(type.id, false)}
                        data-testid={`edit-applies-${type.id}`}
                      />
                      <Label htmlFor={`edit-${type.id}`} className="text-sm">{type.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateField} data-testid="save-field-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomFields;
